import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { useFieldConfig } from "../hooks/useFieldConfig";
import { useFieldEngine } from "../hooks/useFieldEngine";
import { useFormSubmit } from "../hooks/useFormSubmit";
import FieldGroup from "../FieldGroup";
import { DEFAULT_RULE_PROVIDERS } from "../ruleProviders";
import { RENDER_MODE, RENDER_TYPE, READ_ONLY_RENDER_MODES } from "../constants";
import { groupByFieldGroup } from "./groupByFieldGroup";
import { FormFieldsProvider } from "./FormFieldsContext";
import "./styles.scss";
import { testId } from "../../../utils/testIds";

function stripHashSuffix(fieldKey) {
  if (typeof fieldKey !== 'string') return fieldKey;
  return fieldKey.replace(/_[a-f0-9]{6,}$/i, '');
}

function FormEngine({
  templateId,
  templateShortName = null,
  renderMode = RENDER_MODE.CREATE,
  renderType = RENDER_TYPE.WORKKARD,
  initialValues = {},
  onSubmit,
  onCancel,
  onDirtyChange,
  readOnly = false,
  showSubmitButton = true,
  submitLabel = "Save",
  cancelLabel = "Cancel",
  // Callers whose write outlives the onSubmit call -- a dispatch that returns
  // immediately while the request is still in flight -- own the submitting flag
  // themselves and pass it here.
  isSubmitting: isSubmittingProp,
  submittingLabel = "Saving...",
  separator = null,
  footer = null,
  rawRows = null,
  ruleProviders = DEFAULT_RULE_PROVIDERS,
  ruleOverrides = {},
  children = null,
  fieldErrors = null,
}) {
  const { fieldConfigs, isLoading, error: configError } = useFieldConfig(
    templateId,
    renderMode,
    ruleOverrides,
    rawRows,
    renderType,
    ruleProviders,
  );

  const contextValues = useMemo(() => (
    templateShortName ? { __templateShortName: templateShortName } : {}
  ), [templateShortName]);

  const { methods, derivedState } = useFieldEngine(fieldConfigs, initialValues, contextValues);

  const isDirty = methods.formState.isDirty;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  const fieldConfigsRef = useRef(fieldConfigs);
  fieldConfigsRef.current = fieldConfigs;

  useEffect(() => {
    if (!fieldErrors) return;
    const strippedToRealKey = {};
    fieldConfigsRef.current.forEach((c) => {
      const stripped = stripHashSuffix(c.fieldKey);
      if (stripped) strippedToRealKey[stripped] = c.fieldKey;
    });
    Object.entries(fieldErrors).forEach(([fieldKey, message]) => {
      if (!fieldKey || !message) return;
      const realKey = strippedToRealKey[fieldKey]
        ?? strippedToRealKey[stripHashSuffix(fieldKey)]
        ?? fieldKey;
      methods.setError(realKey, { type: 'server', message });
    });
  }, [fieldErrors, methods]);

  const hasFailedSubmitRef = useRef(false);
  const handleInvalidSubmit = useCallback(() => {
    hasFailedSubmitRef.current = true;
  }, []);

  const { handleSubmit, isSubmitting: isFormSubmitting } = useFormSubmit(
    methods,
    fieldConfigs,
    derivedState,
    onSubmit ?? (() => { }),
    handleInvalidSubmit
  );

  const prevVisibleRef = useRef({});
  useEffect(() => {
    const prevVisible = prevVisibleRef.current;
    const nextVisible = {};
    const newlyVisibleKeys = [];

    Object.keys(derivedState).forEach((fieldKey) => {
      const isVisible = derivedState[fieldKey]?.visible !== false;
      nextVisible[fieldKey] = isVisible;
      if (isVisible && prevVisible[fieldKey] === false) {
        newlyVisibleKeys.push(fieldKey);
      }
    });
    prevVisibleRef.current = nextVisible;

    if (hasFailedSubmitRef.current && newlyVisibleKeys.length > 0) {
      methods.trigger(newlyVisibleKeys);
    }
  }, [derivedState, methods]);

  const isSubmitting = isSubmittingProp ?? isFormSubmitting;

  const groupedFields = useMemo(
    () => groupByFieldGroup(fieldConfigs),
    [fieldConfigs]
  );

  const allFieldKeys = useMemo(
    () => fieldConfigs.map((c) => c.fieldKey).filter(Boolean),
    [fieldConfigs]
  );

  if (isLoading) {
    return (
      <div className="loader" role="status" aria-busy="true" data-test={testId(renderType, "form", "loading")}>
        Loading form...
      </div>
    );
  }

  if (configError) {
    return (
      <div className="errorMsg" role="alert" data-test={testId(renderType, "form", "config-error")}>
        {configError}
      </div>
    );
  }

  if (fieldConfigs.length === 0) return null;

  const effectiveRenderMode = readOnly ? RENDER_MODE.DETAIL : renderMode;
  const isReadOnlyRenderMode = READ_ONLY_RENDER_MODES.includes(renderMode);

  return (
    <FormProvider {...methods}>
     <FormFieldsProvider allFieldKeys={allFieldKeys}>
      {children}
      <form
        className="creationForm"
        data-test={testId(renderType, "form")}
        onSubmit={handleSubmit}
        noValidate
      >
        {groupedFields.map(({ groupKey, groupLabel, fields }) => (
          <FieldGroup
            key={groupKey}
            label={groupLabel}
            fields={fields}
            derivedState={derivedState}
            renderMode={effectiveRenderMode}
            renderType={renderType}
          />
        ))}

        {footer}

        <div className="formGroup">
        <div className="wth-2"></div>
        {showSubmitButton && !isReadOnlyRenderMode && (
          <div className="rowFlex alignMiddle">
            <button
              type="submit"
              data-test={testId(renderType, "form", "submit")}
              className="btn-cc btn-cc__primary mrNone"
              disabled={isSubmitting || readOnly}
            >
              {isSubmitting ? submittingLabel : submitLabel}
            </button>
            {/* {separator} */}
            {onCancel && (
              <button
                type="button"
                data-test={testId(renderType, "form", "cancel")}
                className="btn-cc btn-cc__link"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {cancelLabel}
              </button>
            )}
            
          </div>
        )}
        </div>
      </form>
     </FormFieldsProvider>
    </FormProvider>
  );
}

export default FormEngine;
